import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { getAllProviders, getAllHealthcarePayors } from '../../Services/ProviderService';
import { getAllUsers } from '../../Services/UserService';
import { getAllFollowUps, getTrackingById, saveTracking } from '../../Services/TrackingService';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';
import { APPRoutes } from '../../Utils/Route';
import DotLottie from '../../Components/Common/DotLottie';

const defaultTrackingValues = {
  id: null,
  provider_id: '',
  healthcare_payor_id: '',
  follow_up_id: null,
  status_id: null,
  notes: '',
  assignee: '',
  phone: '',
  email: '',
  mode_of_com: null,
  est_date_time: '',
  is_monitoring_completed: null,
  tracking_number: '',
  submission_mode: '',
};

const STATUS_OPTIONS = [
  { id: 1, label: 'Completed' },
  { id: 65, label: 'In-Progress' },
];

const YES_NO_OPTIONS = [
  { id: false, label: 'No' },
  { id: true, label: 'Yes' },
];

const MODE_OF_COMM_OPTIONS = [
  { id: 'Phone', label: 'Phone' },
  { id: 'Email', label: 'Email' },
];

const getLocalDateTimeValue = (date) => {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const parseTrackingApiDateTime = (dateValue) => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';
  return getLocalDateTimeValue(date);
};

const calculateEstimatedDateTime = (days) => {
  const now = new Date();
  const offsetDays = Number(days) || 0;
  const estimated = new Date(now);
  estimated.setDate(estimated.getDate() + offsetDays);
  return getLocalDateTimeValue(estimated);
};

const mapTrackingApiToForm = (data) => ({
  id: data?.id ?? data?.Id ?? null,
  provider_id: data?.provider_id ?? data?.ProviderId ?? '',
  healthcare_payor_id: data?.healthcare_payor_id ?? data?.HealthCarePayorId ?? '',
  follow_up_id: data?.follow_up_id ?? data?.FollowUpId ?? null,
  status_id: data?.status_id ?? data?.StatusId ?? null,
  notes: data?.notes ?? data?.Notes ?? '',
  assignee: data?.assignee ?? data?.Assignee ?? '',
  phone: data?.phone ?? data?.Phone ?? '',
  email: data?.email ?? data?.Email ?? '',
  mode_of_com: data?.mode_of_com ?? data?.ModeOfCom ?? null,
  est_date_time: parseTrackingApiDateTime(data?.est_date_time ?? data?.EstDateTime ?? ''),
  is_monitoring_completed: data?.is_monitoring_completed ?? data?.IsMonitoringCompleted ?? null,
  tracking_number: data?.tracking_number ?? data?.TrackingNumber ?? '',
  submission_mode: data?.submission_mode ?? data?.SubmissionMode ?? '',
});

const extractTrackingFormValues = (payload) => {
  const item = Array.isArray(payload?.data) ? payload.data[0] : payload?.data ?? payload;
  if (!item) return null;

  const firstTracking = Array.isArray(item.trackings)
    ? item.trackings[0]
    : Array.isArray(item.tracks)
      ? item.tracks[0]
      : Array.isArray(item.Trackings)
        ? item.Trackings[0]
        : item.trackings ?? item.tracks ?? item.Trackings ?? item;

  if (!firstTracking) return null;

  return mapTrackingApiToForm({
    ...firstTracking,
    provider_id:
      firstTracking?.provider_id ?? firstTracking?.ProviderId ?? item?.provider?.id ?? item?.provider?.Id,
    healthcare_payor_id:
      firstTracking?.healthcare_payor_id ?? firstTracking?.HealthcarePayorId ?? firstTracking?.healthcarePayorId ??
      item?.healthcare_payor?.id ?? item?.healthcare_payor?.Id ?? item?.healthcarePayorId,
  });
};

const extractTrackingTabValues = (payload) => {
  const item = Array.isArray(payload?.data) ? payload.data[0] : payload?.data ?? payload;
  if (!item) return {};

  const tracks = Array.isArray(item.trackings)
    ? item.trackings
    : Array.isArray(item.tracks)
      ? item.tracks
      : Array.isArray(item.Trackings)
        ? item.Trackings
        : item.trackings ?? item.tracks ?? item.Trackings ?? [];

  if (!Array.isArray(tracks)) return {};

  return tracks.reduce((acc, track) => {
    const followUpId = track?.follow_up_id ?? track?.FollowUpId ?? track?.followUpId ?? null;

    if (followUpId == null) return acc;

    acc[followUpId] = {
      ...mapTrackingApiToForm(track),
      follow_up_id: followUpId,
      saved: true,
      id: track?.id ?? track?.Id ?? null,
    };

    return acc;
  }, {});
};

const buildTrackingPayload = (values) => {
  const trackingId = values?.id ?? null;

  return {
    id: trackingId,
    provider_id: values.provider_id || null,
    healthcare_payor_id: values.healthcare_payor_id || null,
    follow_up_id: values.follow_up_id || null,
    status_id: values.status_id || null,
    notes: values.notes || null,
    assignee: values.assignee || null,
    phone: values.phone || null,
    email: values.email || null,
    mode_of_com: values.mode_of_com || null,
    est_date_time: values.est_date_time || null,
    is_monitoring_completed:
      values.is_monitoring_completed === null ? null : values.is_monitoring_completed,
    type: trackingId ? 'update' : 'create',
    tracking_number: values.tracking_number || null,
    submission_mode: values.submission_mode || null,
  };
};

export const buildActiveTrackingFormValues = ({ defaultValues, sharedValues, tabValues, activeTabKey, followUpId }) => ({
  ...defaultValues,
  ...sharedValues,
  ...(tabValues?.[activeTabKey] || {}),
  follow_up_id: followUpId ?? null,
});

export default function TrackingForm() {
  const navigate = useNavigate();
  const params = useParams();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken]
  );
  const trackingId = params?.id ?? null;
  const isEditMode = Boolean(trackingId);
  const requiredPermission = isEditMode ? PermissionCodes.CREDENTIALING_TRACKING_EDIT : PermissionCodes.CREDENTIALING_TRACKING_CREATE;
  const canAccessPage = permissionHas(userPermissionCodes, requiredPermission, decodedToken);

  const [followUps, setFollowUps] = React.useState([]);
  const [followUpLoading, setFollowUpLoading] = React.useState(true);
  const [providers, setProviders] = React.useState([]);
  const [payors, setPayors] = React.useState([]);
  const [assignees, setAssignees] = React.useState([]);
  const [activeTabIndex, setActiveTabIndex] = React.useState(0);
  const [initialValues, setInitialValues] = React.useState(defaultTrackingValues);
  const [sharedFieldValues, setSharedFieldValues] = React.useState({
    provider_id: '',
    healthcare_payor_id: '',
    assignee: '',
    phone: '',
    email: '',
    tracking_number: '',
  });
  const [tabFieldValues, setTabFieldValues] = React.useState({});
  const sharedFieldValuesRef = React.useRef(sharedFieldValues);
  const tabFieldValuesRef = React.useRef(tabFieldValues);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      provider_id: Yup.string().trim().required('Provider is required.'),
      healthcare_payor_id: Yup.string().trim().required('Payor is required.'),
      assignee: Yup.string().trim().required('Assignee is required.'),
      status_id: Yup.number().nullable().required('Status is required.'),
    }),
    onSubmit: async (values) => {
      setSubmitError('');
      try {
        setSaving(true);
        const response = await saveTracking(buildTrackingPayload(values), token);
        if (response?.is_success) {
          const currentFollowUpId = values.follow_up_id ?? followUps[activeTabIndex]?.id ?? null;
          if (currentFollowUpId !== null) {
            setTabFieldValues((prev) => ({
              ...prev,
              [currentFollowUpId]: {
                ...(prev[currentFollowUpId] || {}),
                ...values,
                follow_up_id: currentFollowUpId,
                saved: true,
              },
            }));
          }

          const lastTabIndex = tabItems.length - 1;
          const isCompleted = Number(values.status_id) === 1;
          const isMonitoringCompleted =
            values.is_monitoring_completed === true ||
            values.is_monitoring_completed === 'true' ||
            values.is_monitoring_completed === 1;
          toast.success(response?.message || 'Monitoring record saved successfully.');

          if (!isMonitoringCompleted && isCompleted && activeTabIndex < lastTabIndex) {
            const nextIndex = activeTabIndex + 1;
            setActiveTabIndex(nextIndex);
            const nextFollowUpId = followUps[nextIndex]?.id ?? null;
            setFieldValue('follow_up_id', nextFollowUpId, false);
          } else {
            const idParam = encodeURIComponent(`${values.provider_id}~${values.healthcare_payor_id}`);
            navigate(APPRoutes.CREDENTIALING_TRACKING_VIEW.replace(':id', idParam));
          }
        } else {
          setSubmitError(response?.message || 'Unable to save tracking.');
        }
      } catch (error) {
        console.error(error);
        setSubmitError('Unable to save tracking.');
      } finally {
        setSaving(false);
      }
    },
  });

  const { values, setFieldValue, setValues, touched, errors, handleChange, handleBlur, handleSubmit } = formik;
  const currentTabKey = followUps[activeTabIndex]?.id ?? activeTabIndex;

  const enabledTabIds = React.useMemo(() => {
    const ids = new Set();
    if (!followUps.length) return ids;

    for (let i = 0; i < followUps.length; i++) {
      const fId = followUps[i]?.id;
      ids.add(fId);

      const tabVal = tabFieldValues[fId] || (i === activeTabIndex ? values : null);
      const isSaved = Boolean(tabVal?.saved || tabVal?.follow_up_id != null || tabVal?.id != null);
      const isCompleted = Number(tabVal?.status_id) === 1;
      const isMonitoringCompleted =
        tabVal?.is_monitoring_completed === true ||
        tabVal?.is_monitoring_completed === 'true' ||
        tabVal?.is_monitoring_completed === 1;

      // 1. If monitoring is marked as completed at this step, no next follow-up steps are allowed
      if (isMonitoringCompleted) {
        break;
      }

      // 2. If this step is not saved or not completed, do not allow going to next follow-up
      if (!isSaved || !isCompleted) {
        break;
      }
    }

    return ids;
  }, [followUps, tabFieldValues, activeTabIndex, values]);

  const isTabClickable = React.useCallback(
    (followUpId, index) => {
      if (index === 0) return true;
      return enabledTabIds.has(followUpId);
    },
    [enabledTabIds]
  );

  React.useEffect(() => {
    if (!followUps.length || !enabledTabIds.size) return;
    const currentFollowUpId = followUps[activeTabIndex]?.id;
    if (currentFollowUpId && !enabledTabIds.has(currentFollowUpId)) {
      let lastValidIndex = 0;
      for (let i = followUps.length - 1; i >= 0; i--) {
        if (enabledTabIds.has(followUps[i]?.id)) {
          lastValidIndex = i;
          break;
        }
      }
      setActiveTabIndex(lastValidIndex);
    }
  }, [enabledTabIds, activeTabIndex, followUps]);

  const updateSharedFieldValue = React.useCallback((field, value) => {
    setSharedFieldValues((prev) => ({ ...prev, [field]: value }));
    setFieldValue(field, value, false);
  }, [setFieldValue]);

  const updateTabFieldValue = React.useCallback((field, value, tabKey = currentTabKey) => {
    setTabFieldValues((prev) => ({
      ...prev,
      [tabKey]: {
        ...(prev[tabKey] || {}),
        [field]: value,
      },
    }));
    setFieldValue(field, value, false);
  }, [currentTabKey, setFieldValue]);

  const loadFollowUps = React.useCallback(async () => {
    if (!token) return;
    setFollowUpLoading(true);
    try {
      const response = await getAllFollowUps(token);
      if (response?.is_success) {
        setFollowUps(response.data || []);
      } else {
        toast.error(response?.message || 'Unable to load follow-up steps.');
        setFollowUps([]);
      }
    } catch (err) {
      console.error(err);
      toast.error('Unable to load follow-up steps.');
      setFollowUps([]);
    } finally {
      setFollowUpLoading(false);
    }
  }, [token]);

  const loadTracking = React.useCallback(async () => {
    if (!token || !isEditMode || !trackingId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await getTrackingById(trackingId, token);
      if (response?.is_success) {
        const values = extractTrackingFormValues(response);
        const tabValues = extractTrackingTabValues(response);
        if (values) {
          setInitialValues((prev) => ({
            ...prev,
            ...values,
          }));

          setSharedFieldValues((prev) => ({
            ...prev,
            provider_id: values.provider_id || prev.provider_id,
            healthcare_payor_id: values.healthcare_payor_id || prev.healthcare_payor_id,
            assignee: values.assignee || prev.assignee,
            phone: values.phone || prev.phone,
            email: values.email || prev.email,
            tracking_number: values.tracking_number || prev.tracking_number,
          }));

          setTabFieldValues((prev) => ({
            ...prev,
            ...tabValues,
          }));
        } else {
          toast.error('Unable to parse tracking record.');
        }
      } else {
        toast.error(response?.message || 'Unable to load tracking record.');
      }
    } catch (error) {
      console.error(error);
      toast.error('Unable to load tracking record.');
    } finally {
      setLoading(false);
    }
  }, [isEditMode, token, trackingId]);

  const loadProviders = React.useCallback(async () => {
    if (!token) return;
    try {
      const response = await getAllProviders(token);
      const items =
        response?.data?.Providers ??
        response?.data?.providers ??
        response?.data ??
        response?.providers ??
        response;
      setProviders(Array.isArray(items) ? items : []);
    } catch (error) {
      console.error(error);
      setProviders([]);
    }
  }, [token]);

  const loadPayors = React.useCallback(async () => {
    if (!token) return;
    try {
      const response = await getAllHealthcarePayors(token);
      const items = response?.data?.payors ?? response?.data ?? response;
      setPayors(Array.isArray(items) ? items : []);
    } catch (error) {
      console.error(error);
      setPayors([]);
    }
  }, [token]);

  const loadAssignees = React.useCallback(async () => {
    if (!token) return;
    try {
      const response = await getAllUsers(token);
      const payload = response?.data ?? response;
      const items = Array.isArray(payload?.Users)
        ? payload.Users
        : Array.isArray(payload)
        ? payload
        : [];
      setAssignees(items);
    } catch (error) {
      console.error(error);
      setAssignees([]);
    }
  }, [token]);

  React.useEffect(() => {
    loadFollowUps();
    loadTracking();
    loadProviders();
    loadPayors();
    loadAssignees();
  }, [loadFollowUps, loadTracking, loadProviders, loadPayors, loadAssignees]);

  

  React.useEffect(() => {
    sharedFieldValuesRef.current = sharedFieldValues;
  }, [sharedFieldValues]);

  React.useEffect(() => {
    tabFieldValuesRef.current = tabFieldValues;
  }, [tabFieldValues]);

  React.useEffect(() => {
    const nextValues = buildActiveTrackingFormValues({
      defaultValues: defaultTrackingValues,
      sharedValues: sharedFieldValuesRef.current,
      tabValues: tabFieldValuesRef.current,
      activeTabKey: currentTabKey,
      followUpId: followUps[activeTabIndex]?.id ?? null,
    });

    setValues(nextValues, false);
  }, [activeTabIndex, currentTabKey, followUps, setValues]);

  React.useEffect(() => {
    const currentStep = followUps[activeTabIndex];
    if (!currentStep) return;
    const computedDateTime = calculateEstimatedDateTime(currentStep.est_days);
    const activeTabValues = tabFieldValues[currentTabKey] || {};
    if (!isEditMode || !activeTabValues.est_date_time) {
      if (activeTabValues.est_date_time !== computedDateTime) {
        updateTabFieldValue('est_date_time', computedDateTime, currentTabKey);
      }
    }
  }, [followUps, activeTabIndex, currentTabKey, isEditMode, tabFieldValues, updateTabFieldValue]);

  React.useEffect(() => {
    if (!followUps.length) return;
    const activeTabValues = tabFieldValues[currentTabKey] || {};
    const selectedIndex = followUps.findIndex((item) => item.id === Number(activeTabValues.follow_up_id));
    if (selectedIndex >= 0 && selectedIndex !== activeTabIndex) {
      setActiveTabIndex(selectedIndex);
      return;
    }

    if (!isEditMode && !activeTabValues.follow_up_id) {
      const defaultFollowUpId = followUps[0]?.id ?? null;
      updateTabFieldValue('follow_up_id', defaultFollowUpId, currentTabKey);
      setActiveTabIndex(0);
    }
  }, [followUps, currentTabKey, activeTabIndex, isEditMode, tabFieldValues, updateTabFieldValue]);

  const tabItems = followUps.map((item) => ({
    id: `follow-up-${item.id}`,
    label: item.name,
    meta: item,
  }));

  const handleFollowUpChange = (followUpId) => {
    const selectedIndex = followUps.findIndex((item) => item.id === Number(followUpId));
    const nextTabKey = followUps[selectedIndex]?.id ?? selectedIndex;
    updateTabFieldValue('follow_up_id', followUpId, nextTabKey);
    if (selectedIndex >= 0) {
      setActiveTabIndex(selectedIndex);
    }
  };

  const isFirstTab = activeTabIndex === 0;
  const actionLabel = isEditMode ? 'Save' : isFirstTab ? 'Start Tracking' : 'Save';

  const renderTabContent = (tab) => {
    const step = tab?.meta || {};

    return (
      <>
        <div className="mb-4">
          <h5 className="fw-bold mb-2">{tab.label}</h5>
          <p className="text-muted mb-0">Use the form below to configure this follow-up step.</p>
        </div>

        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <SearchableDropdown
              label="Provider"
              placeholder="Select provider"
              options={providers.map((provider) => {
                const id =
                  provider.id ??
                  provider.Id ??
                  provider.provider_id ??
                  provider.ProviderId ??
                  provider.providerId ??
                  provider.payor_id ??
                  provider.payorId;
                const label =
                  provider.provider_name ??
                  provider.ProviderName ??
                  (`${provider.first_name || ''} ${provider.last_name || ''}`.trim() ||
                    provider.name) ??
                  provider.Name ??
                  'Provider';
                return { value: String(id ?? label), label };
              })}
              value={values.provider_id}
              onChange={(value) => updateSharedFieldValue('provider_id', value)}
              className="w-100"
              disabled={!isFirstTab}
              showSearch={false}
              required
            />
            {touched.provider_id && errors.provider_id ? (
              <div className="text-danger small mt-1">{errors.provider_id}</div>
            ) : null}
          </div>
          <div className="col-12 col-lg-6">
            <SearchableDropdown
              label="Payor"
              placeholder="Select payor"
              options={payors.map((payor) => {
                const id =
                  payor.id ??
                  payor.Id ??
                  payor.healthcare_payor_id ??
                  payor.healthcarePayorId ??
                  payor.payor_id ??
                  payor.payorId ??
                  payor.ProviderId;
                const label =
                  payor.payor_name ?? payor.payorName ?? payor.name ?? payor.Name ?? 'Payor';
                return { value: String(id ?? label), label };
              })}
              value={values.healthcare_payor_id}
              onChange={(value) => updateSharedFieldValue('healthcare_payor_id', value)}
              className="w-100"
              disabled={!isFirstTab}
              showSearch={false}
              required
            />
            {touched.healthcare_payor_id && errors.healthcare_payor_id ? (
              <div className="text-danger small mt-1">{errors.healthcare_payor_id}</div>
            ) : null}
          </div>
          <div className="col-12 col-lg-4">
            <SearchableDropdown
              label="Assignee"
              placeholder="Select assignee"
              options={assignees.map((staff) => {
                const id =
                  staff.id ??
                  staff.Id ??
                  staff.staff_id ??
                  staff.StaffId ??
                  staff.user_id ??
                  staff.UserId ??
                  staff.userId;
                const label =
                  staff.full_name ??
                  staff.FullName ??
                  ((`${staff.first_name || staff.FirstName || ''} ${staff.last_name || staff.LastName || ''}`.trim()) ||
                    staff.user_name ||
                    staff.UserName ||
                    staff.username ||
                    staff.name ||
                    staff.Name) ??
                  'Assignee';
                return { value: String(id ?? label), label };
              })}
              value={values.assignee}
              onChange={(value) => updateSharedFieldValue('assignee', value)}
              className="w-100"
              required
            />
            {touched.assignee && errors.assignee ? (
              <div className="text-danger small mt-1">{errors.assignee}</div>
            ) : null}
          </div>
          <div className="col-12 col-lg-4">
            <label className="form-label">Estimated Date/Time</label>
            <input
              type="datetime-local"
              className="form-control"
              value={values.est_date_time}
              readOnly
            />
          </div>
          <div className="col-12 col-lg-4">
            <SearchableDropdown
              label="Mode of Communication"
              placeholder="Select communication mode"
              options={MODE_OF_COMM_OPTIONS.map((option) => ({ value: option.id, label: option.label }))}
              value={values.mode_of_com}
              onChange={(value) => updateTabFieldValue('mode_of_com', value || null)}
              className="w-100"
              showSearch={false}
            />
          </div>
          <div className="col-12 col-lg-4">
            <SearchableDropdown
              label="Submission Mode"
              placeholder="Select submission mode"
              options={[
                { value: 'Portal', label: 'Portal' },
                { value: 'Fax', label: 'Fax' },
                { value: 'Email', label: 'Email' }
              ]}
              value={values.submission_mode}
              onChange={(value) => updateTabFieldValue('submission_mode', value || '')}
              className="w-100"
              showSearch={false}
            />
          </div>
          <div className="col-12 col-lg-4">
            <SearchableDropdown
              label="Status"
              placeholder="Select status"
              options={STATUS_OPTIONS.map((option) => ({ value: String(option.id), label: option.label }))}
              value={values.status_id ? String(values.status_id) : ''}
              onChange={(value) => updateTabFieldValue('status_id', Number(value) || null)}
              className="w-100"
              showSearch={false}
              required
            />
            {touched.status_id && errors.status_id ? (
              <div className="text-danger small mt-1">{errors.status_id}</div>
            ) : null}
          </div>
          <div className="col-12 col-lg-4">
            <label className="form-label">Submission Email</label>
            <input
              type="email"
              className="form-control"
              name="email"
              value={values.email}
              onChange={(event) => updateSharedFieldValue('email', event.target.value)}
              placeholder="Enter submission email"
            />
          </div>
          <div className="col-12 col-lg-4">
            <label className="form-label">Phone</label>
            <input
              type="tel"
              className="form-control"
              name="phone"
              value={values.phone}
              onChange={(event) => updateSharedFieldValue('phone', event.target.value)}
              placeholder="Enter phone number"
            />
          </div>
          <div className="col-12 col-lg-4">
            <label className="form-label">Tracking Number</label>
            <input
              type="text"
              className="form-control"
              name="tracking_number"
              value={values.tracking_number}
              onChange={(event) => updateSharedFieldValue('tracking_number', event.target.value)}
              placeholder="Enter tracking number"
            />
          </div>
          <div className="col-12 col-lg-4">
            <SearchableDropdown
              label="Monitoring Completed"
              placeholder="Select Option"
              options={YES_NO_OPTIONS.map((option) => ({ value: String(option.id), label: option.label }))}
              value={values.is_monitoring_completed === null ? '' : String(values.is_monitoring_completed)}
              onChange={(value) => updateTabFieldValue('is_monitoring_completed', value === '' ? null : value === 'true')}
              className="w-100"
              showSearch={false}
            />
          </div>
          <div className="col-12 col-lg-4">
            <label className="form-label">Remaining Hours</label>
            <input type="text" className="form-control" value={step.rem_hours ?? ''} readOnly />
          </div>
          <div className="col-12 col-lg-4">
            <label className="form-label">Estimated Days</label>
            <input type="text" className="form-control" value={step.est_days ?? ''} readOnly />
          </div>
          <div className="col-12">
            <label className="form-label">Notes</label>
            <textarea className="form-control" name="notes" value={values.notes} onChange={(event) => updateTabFieldValue('notes', event.target.value)} rows={4} placeholder="Add notes for this follow-up" />
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-4">
          {!isFirstTab && (
            <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(APPRoutes.CREDENTIALING_TRACKING)} disabled={saving}>
              Cancel
            </button>
          )}
          <button type="button" className="btn hc-bg-primary text-white" onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                {actionLabel}
              </>
            ) : (
              actionLabel
            )}
          </button>
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <DotLottie/>
      </div>
    );
  }

  if (!canAccessPage) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger">You do not have permission to access this page.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      <div className="rounded-4 bg-white p-2 p-md-4 shadow-md border-0">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              className="btn btn-light border rounded-circle shadow-sm"
              onClick={() => navigate(-1)}
              style={{ width: 40, height: 40 }}
            >
              &larr;
            </button>
            <div>
              <h3 className="fw-bold mb-1">{isEditMode ? 'Edit Tracking' : 'Add Tracking'}</h3>
              <p className="text-muted mb-0">Manage tracking details using the vertical follow-up navigation panel.</p>
            </div>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-12 col-lg-3">
            <div className="rounded-4 bg-white p-3 h-100 border">
              <h6 className="fw-bold mb-3">Follow-up steps</h6>
              <ul className="nav nav-pills flex-column gap-3 w-100">
                {followUpLoading ? (
                [...Array(8)].map((_, index) => (
                  <li key={index} className="w-100">
                    <div className="skeleton rounded-3 py-3 w-100" style={{ minHeight: 48 }} />
                  </li>
                ))
              ) : tabItems.length === 0 ? (
                <li className="text-muted small">No follow-up steps available.</li>
              ) : (
                tabItems.map((tab, index) => {
                  const tabId = followUps[index]?.id ?? null;
                  const isActive = index === activeTabIndex;
                  const clickable = isTabClickable(tabId, index);
                  return (
                    <li key={tab.id} className="w-100">
                      <button
                        type="button"
                        className={`nav-link text-start rounded-3 border py-2 w-100 ${
                          isActive ? 'hc-bg-primary text-white' : clickable ? 'bg-light text-secondary' : 'bg-white text-muted'
                        }`}
                        disabled={!clickable}
                        onClick={() => clickable && handleFollowUpChange(tabId)}
                        style={{ cursor: clickable ? 'pointer' : 'not-allowed' }}
                      >
                        {tab.label}
                      </button>
                    </li>
                  );
                })
              )}
              </ul>
            </div>
          </div>

          <div className="col-12 col-lg-9">
            <div className="rounded-4 bg-white p-3 p-md-4 shadow-sm border">
              {followUpLoading ? (
                <div className="row g-3">
                  <div className="col-12 col-lg-6">
                    <div className="skeleton mb-3 w-100" style={{ height: 48 }} />
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="skeleton mb-3 w-100" style={{ height: 48 }} />
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="skeleton mb-3 w-100" style={{ height: 48 }} />
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="skeleton mb-3 w-100" style={{ height: 48 }} />
                  </div>
                  <div className="col-12 col-lg-4">
                    <div className="skeleton mb-3 w-100" style={{ height: 48 }} />
                  </div>
                  <div className="col-12 col-lg-4">
                    <div className="skeleton mb-3 w-100" style={{ height: 48 }} />
                  </div>
                  <div className="col-12 col-lg-4">
                    <div className="skeleton mb-3 w-100" style={{ height: 48 }} />
                  </div>
                  <div className="col-12 col-lg-4">
                    <div className="skeleton mb-3 w-100" style={{ height: 48 }} />
                  </div>
                  <div className="col-12 col-lg-4">
                    <div className="skeleton mb-3 w-100" style={{ height: 48 }} />
                  </div>
                  <div className="col-12 col-lg-4">
                    <div className="skeleton mb-3 w-100" style={{ height: 48 }} />
                  </div>
                  <div className="col-12">
                    <div className="skeleton mb-3 w-100" style={{ height: 120 }} />
                  </div>
                  <div className="col-12 d-flex justify-content-end gap-2 mt-4">
                    <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 42, width: 120 }} />
                    <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 42, width: 120 }} />
                  </div>
                </div>
              ) : tabItems.length === 0 ? (
                <div className="text-muted">No follow-ups were found. Please add follow-up steps first.</div>
              ) : (
                renderTabContent(tabItems[activeTabIndex])
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
